/* GBI · product page, below the fold (B5)
   <gbi-slider>, <gbi-proof>, <gbi-build-video>, <gbi-related>, lazy video stills (video[data-gbi-poster])
   Spec: docs/specs/gbi-product-page-build-spec.md §B5, §C, §I
   Vanilla ES2019, one IIFE, no globals besides the run-once flag. */
(() => {
  'use strict';

  if (window.__gbiPdpSections) return;
  window.__gbiPdpSections = true;

  /* ---------- helpers ---------------------------------------------- */

  const mq = (name, query) => {
    const G = window.GBI;
    if (G && G.mq && G.mq[name]) return G.mq[name];
    return window.matchMedia(query);
  };
  const reduced = () => mq('reduced', '(prefers-reduced-motion: reduce)').matches;
  /* Polish pass §5.1: data saver / 2G = no autoplay (the poster stays; a keyboard user can still play). */
  const saveData = () => {
    const c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    return !!(c && (c.saveData || /(^|-)2g$/.test(c.effectiveType || '')));
  };

  /* onBlocked runs when the browser refuses to play (e.g. iOS Low Power Mode refuses even muted autoplay). */
  const tryPlay = (video, onBlocked) => {
    if (!video) return;
    try {
      const p = video.play();
      if (p && typeof p.catch === 'function') {
        p.catch((err) => { if (onBlocked && err && err.name === 'NotAllowedError') onBlocked(); });
      }
    } catch (e) { if (onBlocked) onBlocked(); }
  };

  /* Wire one video + toggle button. Returns {video, setInView, evaluate}.
     Polish pass (gbi-polish-cards-videos-spec §5.1): autoplay also stops for data saver and while the tab is
     hidden (it resumes when the tab comes back, unless the visitor paused it). The toggle may be invisible
     (class gbi-focus-reveal on the homepage hero, collection banners and the build video): it still works.
     Rev. 2: when the video cannot loop on its own (reduced motion, data saver, or the browser refused
     autoplay, e.g. iOS Low Power Mode) the frame gets .is-manual and the toggle shows for everyone
     (gbi-base.css), so the video is never a still with no way to play it. A 'playing' that the visitor did
     not start (autoplay came back) removes it again. */
  const wireVideo = (frame, toggle, labels, signal) => {
    const video = frame.querySelector('video');
    if (!video) return null;
    const state = { userPaused: false, userPlayed: false, inView: false };

    video.muted = true;
    video.setAttribute('muted', '');
    video.playsInline = true;
    video.removeAttribute('autoplay');
    video.removeAttribute('controls');

    const sync = () => {
      const playing = !video.paused && !video.ended;
      if (toggle) {
        // Icon state rides on data-gbi-playing. With both labels set the name itself changes
        // ("Pause video" / "Play video"), so aria-pressed is dropped (CM-06: never "Pause video, pressed").
        toggle.toggleAttribute('data-gbi-playing', playing);
        if (labels.play && labels.pause) {
          toggle.removeAttribute('aria-pressed');
          toggle.setAttribute('aria-label', playing ? labels.pause : labels.play);
        } else {
          toggle.setAttribute('aria-pressed', playing ? 'true' : 'false');
        }
      }
      frame.classList.toggle('is-playing', playing);
    };

    /* The homepage hero and the collection banners (data-gbi-hero-frame) never go manual: they keep the still
       with no visible button (spec §5.1 table, §6.3), since a photo stands behind them. Keyboard users still
       reach their focus-reveal toggle. */
    /* Karim (2026-10-13): frames with data-gbi-always (the PDP build video) always loop with no visible
       button: data saver does not stop them, and when the browser refuses autoplay (e.g. iOS Low Power Mode)
       they start on the visitor's first tap/click/key anywhere on the page. Reduced motion is still honoured
       (still frame, no button). */
    const always = frame.hasAttribute('data-gbi-always');
    const canManual = !frame.hasAttribute('data-gbi-hero-frame') && !always;
    let gestureArmed = false;
    const armGesture = () => {
      if (!always || gestureArmed) return;
      gestureArmed = true;
      const go = () => {
        ['pointerup', 'touchend', 'click', 'keydown'].forEach((t) => document.removeEventListener(t, go, true));
        gestureArmed = false;
        if (!reduced() && !state.userPaused && video.paused) tryPlay(video);
      };
      ['pointerup', 'touchend', 'click', 'keydown'].forEach((t) => document.addEventListener(t, go, { capture: true, passive: true, signal }));
    };
    const setManual = (on) => { if (canManual) frame.classList.toggle('is-manual', on); };
    video.addEventListener('playing', () => { if (!state.userPlayed) setManual(false); }, { signal });
    video.addEventListener('play', sync, { signal });
    video.addEventListener('pause', sync, { signal });
    video.addEventListener('ended', sync, { signal });
    video.addEventListener('error', () => {
      frame.classList.add('is-error');
      if (toggle) toggle.hidden = true;
      frame.classList.remove('is-playing');
    }, { signal });

    if (toggle) {
      toggle.addEventListener('click', (e) => {
        e.preventDefault();
        if (video.paused) {
          state.userPaused = false;
          state.userPlayed = true;
          tryPlay(video);
        } else {
          state.userPaused = true;
          state.userPlayed = false;
          video.pause();
        }
      }, { signal });
    }

    const evaluate = () => {
      if (state.inView && !document.hidden) {
        const blocked = always ? reduced() : (reduced() || saveData());
        const auto = !blocked && !state.userPaused;
        if (blocked && !state.userPlayed) setManual(true);
        if ((auto || state.userPlayed) && video.paused) tryPlay(video, () => { if (!state.userPlayed) { setManual(true); armGesture(); } });
        /* HD-QA-05: reduced motion switched on mid-session stops an autoplaying video
           (a video the visitor started stays on). */
        else if (!auto && !state.userPlayed && !video.paused) video.pause();
      } else if (!video.paused) {
        video.pause();
      }
    };

    document.addEventListener('visibilitychange', evaluate, { signal });

    sync();
    return {
      video,
      setInView(v) { state.inView = v; evaluate(); },
      evaluate
    };
  };

  const smoothScrollTo = (hash) => {
    if (!hash || hash.charAt(0) !== '#' || hash.length < 2) return false;
    let target = null;
    try { target = document.getElementById(decodeURIComponent(hash.slice(1))); } catch (e) { target = null; }
    if (!target) return false;
    target.scrollIntoView({ behavior: reduced() ? 'auto' : 'smooth', block: 'start' });
    if (!target.hasAttribute('tabindex') && !/^(A|BUTTON|INPUT|SELECT|TEXTAREA)$/.test(target.tagName)) {
      target.setAttribute('tabindex', '-1');
    }
    try { target.focus({ preventScroll: true }); } catch (e) { /* noop */ }
    return true;
  };

  /* ---------- <gbi-slider> ----------------------------------------- */
  /* Horizontal snap track. Arrows ([data-gbi-slider-prev/next] inside
     [data-gbi-slider-nav]) show on desktop only when the track overflows. */

  class GbiSlider extends HTMLElement {
    connectedCallback() { this.setup(); }
    disconnectedCallback() { this.teardown(); }

    teardown() {
      if (this._ac) this._ac.abort();
      if (this._ro) this._ro.disconnect();
      this._ac = null;
      this._ro = null;
    }

    /* Public: call after the track is replaced (gbi-related). */
    refresh() { this.setup(); }

    setup() {
      this.teardown();
      this.track = this.querySelector('.gbi-slider__track');
      this.nav = this.querySelector('[data-gbi-slider-nav]');
      this.prev = this.querySelector('[data-gbi-slider-prev]');
      this.next = this.querySelector('[data-gbi-slider-next]');
      if (!this.track) return;

      const ac = new AbortController();
      this._ac = ac;
      const signal = ac.signal;

      let raf = 0;
      const schedule = () => {
        if (raf) return;
        raf = requestAnimationFrame(() => { raf = 0; this.update(); });
      };

      this.track.addEventListener('scroll', schedule, { passive: true, signal });
      window.addEventListener('resize', schedule, { passive: true, signal });
      if (this.prev) this.prev.addEventListener('click', () => this.step(-1), { signal });
      if (this.next) this.next.addEventListener('click', () => this.step(1), { signal });

      if ('ResizeObserver' in window) {
        this._ro = new ResizeObserver(schedule);
        this._ro.observe(this.track);
      }
      this.update();
    }

    update() {
      const t = this.track;
      if (!t) return;
      const max = t.scrollWidth - t.clientWidth;
      const overflow = max > 2;
      if (this.nav) this.nav.hidden = !overflow;
      if (this.prev) this.prev.disabled = !overflow || t.scrollLeft <= 2;
      if (this.next) this.next.disabled = !overflow || t.scrollLeft >= max - 2;
    }

    step(dir) {
      const t = this.track;
      if (!t) return;
      const item = t.querySelector('.gbi-slider__item');
      const gap = parseFloat(getComputedStyle(t).columnGap) || 0;
      const w = item ? item.getBoundingClientRect().width + gap : t.clientWidth * 0.8;
      const per = Math.max(1, Math.floor((t.clientWidth + gap) / w));
      t.scrollBy({ left: dir * per * w, behavior: reduced() ? 'auto' : 'smooth' });
    }
  }

  /* ---------- <gbi-proof> ------------------------------------------ */
  /* Customer videos autoplay (muted) only while >= 60% in view, never under
     reduced motion; a tap toggles. The rating link loses its href when
     #gbi-reviews is not on the page (feed switched off). */

  class GbiProof extends HTMLElement {
    connectedCallback() { this.setup(); }
    disconnectedCallback() { this.teardown(); }

    teardown() {
      if (this._ac) this._ac.abort();
      if (this._io) this._io.disconnect();
      this._ac = null;
      this._io = null;
    }

    setup() {
      this.teardown();
      const ac = new AbortController();
      this._ac = ac;
      const signal = ac.signal;
      const labels = { play: this.dataset.strPlay || '', pause: this.dataset.strPause || '' };

      this.checkRatingLink();
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.checkRatingLink(), { once: true, signal });
      }

      const players = new Map();
      this.querySelectorAll('[data-gbi-proof-video]').forEach((frame) => {
        const p = wireVideo(frame, frame.querySelector('[data-gbi-proof-toggle]'), labels, signal);
        if (p) players.set(frame, p);
      });
      if (!players.size) return;

      if ('IntersectionObserver' in window) {
        this._io = new IntersectionObserver((entries) => {
          entries.forEach((en) => {
            const p = players.get(en.target);
            if (p) p.setInView(en.isIntersecting && en.intersectionRatio >= 0.6);
          });
        }, { threshold: [0, 0.6, 1] });
        players.forEach((p, frame) => this._io.observe(frame));
      }

      const rm = mq('reduced', '(prefers-reduced-motion: reduce)');
      const onRm = () => players.forEach((p) => p.evaluate());
      if (rm.addEventListener) rm.addEventListener('change', onRm, { signal });
    }

    checkRatingLink() {
      const a = this.querySelector('[data-gbi-proof-rating] a[href]');
      if (!a) return;
      const href = a.getAttribute('href') || '';
      if (href.charAt(0) !== '#') return;
      let target = null;
      try { target = document.getElementById(decodeURIComponent(href.slice(1))); } catch (e) { target = null; }
      if (!target) {
        a.removeAttribute('href');
        a.classList.add('is-static');
        return;
      }
      if (!a.dataset.gbiScrollBound) {
        a.dataset.gbiScrollBound = '1';
        a.addEventListener('click', (e) => {
          if (smoothScrollTo(a.getAttribute('href'))) e.preventDefault();
        });
      }
    }
  }

  /* ---------- <gbi-build-video> ------------------------------------ */
  /* Autoplay (muted, loop) while in view; reduced motion or data saver = poster (the toggle, visible on
     keyboard focus only since the polish pass, can still play it). The CTA smooth-scrolls to its #target. */

  class GbiBuildVideo extends HTMLElement {
    connectedCallback() { this.setup(); }
    disconnectedCallback() { this.teardown(); }

    teardown() {
      if (this._ac) this._ac.abort();
      if (this._io) this._io.disconnect();
      this._ac = null;
      this._io = null;
    }

    setup() {
      this.teardown();
      const ac = new AbortController();
      this._ac = ac;
      const signal = ac.signal;
      const labels = { play: this.dataset.strPlay || '', pause: this.dataset.strPause || '' };

      this.querySelectorAll('[data-gbi-scroll]').forEach((a) => {
        a.addEventListener('click', (e) => {
          if (smoothScrollTo(a.getAttribute('href'))) e.preventDefault();
        }, { signal });
      });

      const frame = this.querySelector('[data-gbi-build-frame]');
      if (!frame) return;
      /* "Always loops" is the PDP build video only. The homepage hero and the collection banners
         (data-gbi-hero-frame) reuse this element but keep their decided behaviour: still image with data
         saver, reduced motion or refused autoplay, no button (polish spec D8, theme-notes handover note). */
      if (!frame.hasAttribute('data-gbi-hero-frame')) frame.setAttribute('data-gbi-always', '');
      const player = wireVideo(frame, frame.querySelector('[data-gbi-build-toggle]'), labels, signal);
      if (!player) return;

      if ('IntersectionObserver' in window) {
        this._io = new IntersectionObserver((entries) => {
          entries.forEach((en) => player.setInView(en.isIntersecting && en.intersectionRatio >= 0.25));
        }, { threshold: [0, 0.25, 0.5] });
        this._io.observe(frame);
      } else {
        player.setInView(true);
      }

      const rm = mq('reduced', '(prefers-reduced-motion: reduce)');
      if (rm.addEventListener) rm.addEventListener('change', () => player.evaluate(), { signal });
    }
  }

  /* ---------- <gbi-related> ---------------------------------------- */
  /* Fetches data-url (recommendations endpoint + section_id) when within
     400 px of the viewport, swaps .gbi-related__track in. 0 products = the
     whole section is hidden (editor: a note instead). */

  class GbiRelated extends HTMLElement {
    connectedCallback() {
      if (this._io) this._io.disconnect();
      if (this.dataset.loaded === 'true') {
        this.finish(this.querySelector('.gbi-related__track'));
        return;
      }
      if (this._loading) return;
      if ('IntersectionObserver' in window) {
        this._io = new IntersectionObserver((entries) => {
          if (entries.some((en) => en.isIntersecting)) {
            this._io.disconnect();
            this._io = null;
            this.load();
          }
        }, { rootMargin: '400px 0px' });
        this._io.observe(this);
      } else {
        this.load();
      }
    }

    disconnectedCallback() {
      if (this._io) this._io.disconnect();
      this._io = null;
    }

    load() {
      /* Speed batch 2 (B2-5): the loading shimmer runs from here on. gbi-pdp-sections.css pauses it while
         the element has no .is-near (the section is still more than 400 px from the screen). */
      this.classList.add('is-near');
      const url = this.dataset.url;
      if (!url || this._loading) return;
      this._loading = true;
      fetch(String(url), { credentials: 'same-origin', headers: { Accept: 'text/html' } })
        .then((res) => (res.ok ? res.text() : Promise.reject(new Error(String(res.status)))))
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const fresh = doc.querySelector('.gbi-related__track');
          const current = this.querySelector('.gbi-related__track');
          if (!fresh || !current || !fresh.querySelector('li')) {
            this.empty();
            return;
          }
          const node = document.importNode(fresh, true);
          current.replaceWith(node);
          this.dataset.loaded = 'true';
          this.finish(node);
        })
        .catch(() => this.empty())
        .then(() => { this._loading = false; });
    }

    finish(track) {
      if (!track || !track.querySelector('li')) {
        this.empty();
        return;
      }
      const slider = this.querySelector('gbi-slider');
      if (slider && typeof slider.refresh === 'function') slider.refresh();
    }

    empty() {
      const note = this.querySelector('[data-gbi-related-empty]');
      if (this.dataset.designMode === 'true' && note) {
        const slider = this.querySelector('gbi-slider');
        const track = this.querySelector('.gbi-related__track');
        if (track) track.remove();
        if (slider) slider.hidden = true;
        note.hidden = false;
        return;
      }
      const section = this.closest('.shopify-section') || this.closest('.gbi-s');
      if (section) {
        section.hidden = true;
        section.style.display = 'none';
      }
    }
  }

  /* ---------- details 01 fallback: 6-line clamp + Read more (DES-12) ---------- */

  const phone = () => mq('phone', '(max-width: 749px)').matches;

  const measureClamp = (box) => {
    const btn = box.parentElement && box.parentElement.querySelector('[data-gbi-clamp-toggle]');
    if (box.dataset.gbiClampOpen === 'true') return;
    if (!btn || !phone()) {
      box.classList.remove('is-clamped');
      if (btn) btn.hidden = true;
      return;
    }
    box.classList.add('is-clamped');
    const over = box.scrollHeight > box.clientHeight + 2;
    if (!over) box.classList.remove('is-clamped');
    btn.hidden = !over;
  };

  const initClamps = (scope) => {
    (scope || document).querySelectorAll('[data-gbi-clamp]').forEach((box) => {
      if (!box.__gbiClamp) {
        box.__gbiClamp = true;
        const btn = box.parentElement && box.parentElement.querySelector('[data-gbi-clamp-toggle]');
        if (btn) {
          btn.addEventListener('click', () => {
            box.dataset.gbiClampOpen = 'true';
            box.classList.remove('is-clamped');
            btn.setAttribute('aria-expanded', 'true');
            btn.hidden = true;
            if (!box.hasAttribute('tabindex')) box.setAttribute('tabindex', '-1');
            try { box.focus({ preventScroll: true }); } catch (e) { box.focus(); }
          });
        }
      }
      measureClamp(box);
    });
  };

  const onReady = () => {
    initClamps(document);
    let t = null;
    window.addEventListener('resize', () => {
      clearTimeout(t);
      t = setTimeout(() => document.querySelectorAll('[data-gbi-clamp]').forEach(measureClamp), 150);
    });
    /* the clamp sits in a <details>: measure again when it opens */
    document.addEventListener('toggle', (e) => {
      const d = e.target;
      if (d && d.tagName === 'DETAILS' && d.open) d.querySelectorAll('[data-gbi-clamp]').forEach(measureClamp);
    }, true);
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => document.querySelectorAll('[data-gbi-clamp]').forEach(measureClamp)).catch(() => {});
    }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', onReady);
  else onReady();
  document.addEventListener('shopify:section:load', (e) => initClamps(e.target));

  /* ---------- lazy video stills (speed audit #13) ---------------------- */
  /* <video data-gbi-poster="url 640w, url 960w, …"> (the "Built to be seen" video, the gallery video) gets
     its poster only when it comes near the screen, so the stills no longer download right after the main
     photo. Watching starts once the page's high-priority images (the main photo) have loaded. Phones take
     the smallest file; wider screens the smallest that covers the box at up to 2x. A video that starts
     loading first (a tap, the gallery) gets its still at once, so it never plays without one. */

  let posterIo = null;

  const pickPoster = (video, list) => {
    const set = list.split(',').map((part) => {
      const m = part.trim().match(/^(\S+)\s+(\d+)w$/);
      return m ? { url: m[1], w: Number(m[2]) } : null;
    }).filter(Boolean).sort((a, b) => a.w - b.w);
    if (!set.length) return '';
    if (phone()) return set[0].url;
    const box = video.getBoundingClientRect().width || window.innerWidth;
    const need = box * Math.min(window.devicePixelRatio || 1, 2);
    const fit = set.find((c) => c.w >= need);
    return (fit || set[set.length - 1]).url;
  };

  /* QA-B1-01: from 990 px the gallery stacks every slide in one frame and hides the others with
     visibility:hidden, which IntersectionObserver ignores. A gallery video on such a slide is "held":
     its still loads when the slide shows (class change, or the layout turns into the phone/tablet strip)
     or when the pointer or keyboard focus enters the gallery, one step before it can show. */
  const held = new Set();
  const unseen = (video) => {
    const slide = video.closest('.gbi-gallery__slide');
    return !!slide && (slide.hidden || window.getComputedStyle(slide).visibility === 'hidden');
  };

  const setPoster = (video) => {
    held.delete(video);
    if (video.__gbiHold) { video.__gbiHold(); video.__gbiHold = null; }
    const list = video.getAttribute('data-gbi-poster');
    if (list === null) return;
    video.removeAttribute('data-gbi-poster');
    if (posterIo) posterIo.unobserve(video);
    const url = pickPoster(video, list);
    if (url) video.setAttribute('poster', url);
  };

  const recheck = (video, intent) => {
    if (held.has(video) && (intent || !unseen(video))) setPoster(video);
  };

  const hold = (video) => {
    held.add(video);
    if (video.__gbiHold) return;
    const slide = video.closest('.gbi-gallery__slide');
    const gallery = video.closest('gbi-gallery') || slide;
    const check = () => recheck(video, false);
    const intent = () => recheck(video, true);
    const mo = 'MutationObserver' in window ? new MutationObserver(check) : null;
    if (mo) mo.observe(slide, { attributes: true, attributeFilter: ['class', 'hidden'] });
    gallery.addEventListener('pointerenter', intent);
    gallery.addEventListener('focusin', intent);
    video.__gbiHold = () => {
      if (mo) mo.disconnect();
      gallery.removeEventListener('pointerenter', intent);
      gallery.removeEventListener('focusin', intent);
    };
  };

  const wideMq = window.matchMedia('(min-width: 990px)');
  const onWide = () => held.forEach((video) => recheck(video, false));
  if (wideMq.addEventListener) wideMq.addEventListener('change', onWide);
  else if (wideMq.addListener) wideMq.addListener(onWide);

  const afterHighImages = (fn) => {
    const pending = Array.prototype.filter.call(document.querySelectorAll('img[fetchpriority="high"]:not([loading="lazy"])'), (img) => !img.complete);
    let left = pending.length;
    if (!left) { fn(); return; }
    const one = () => { left -= 1; if (left === 0) fn(); };
    pending.forEach((img) => {
      let seen = false;
      const done = () => { if (!seen) { seen = true; one(); } };
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', done, { once: true });
    });
  };

  const lazyPosters = (scope) => {
    const videos = Array.prototype.filter.call((scope || document).querySelectorAll('video[data-gbi-poster]'), (v) => !v.__gbiPoster);
    if (!videos.length) return;
    const watch = [];
    videos.forEach((video) => {
      video.__gbiPoster = true;
      const now = () => setPoster(video);
      if (!video.paused || video.readyState > 0 || video.networkState === 2 || !('IntersectionObserver' in window)) { now(); return; }
      video.addEventListener('loadstart', now, { once: true });
      video.addEventListener('play', now, { once: true });
      watch.push(video);
    });
    if (!watch.length) return;
    afterHighImages(() => {
      if (!posterIo) {
        posterIo = new IntersectionObserver((entries) => {
          entries.forEach((en) => {
            if (!en.isIntersecting) held.delete(en.target);
            else if (unseen(en.target)) hold(en.target);
            else setPoster(en.target);
          });
        }, { rootMargin: '100% 0px' });
      }
      watch.forEach((video) => { if (video.hasAttribute('data-gbi-poster')) posterIo.observe(video); });
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => lazyPosters(document));
  else lazyPosters(document);
  document.addEventListener('shopify:section:load', (e) => lazyPosters(e.target));

  /* ---------- define ----------------------------------------------- */

  const define = (name, cls) => {
    if (!customElements.get(name)) customElements.define(name, cls);
  };
  define('gbi-slider', GbiSlider);
  define('gbi-proof', GbiProof);
  define('gbi-build-video', GbiBuildVideo);
  define('gbi-related', GbiRelated);
})();
